import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layouts/AdminLayout';
import {
  Search,
  Plus,
  Building,
  Mail,
  Phone,
  MoreVertical,
  FolderKanban,
  ExternalLink
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Label } from '../../components/ui/label';

const ClientsPage = () => {
  const { api } = useAuth();
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [newClient, setNewClient] = useState({
    company_name: '',
    primary_contact_name: '',
    primary_contact_email: '',
    primary_contact_phone: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clientsRes, projectsRes] = await Promise.all([
        api.get('/clients'),
        api.get('/projects'),
      ]);
      setClients(clientsRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const createClient = async (e) => {
    e.preventDefault();
    try {
      await api.post('/clients', newClient);
      setCreateOpen(false);
      setNewClient({
        company_name: '',
        primary_contact_name: '',
        primary_contact_email: '',
        primary_contact_phone: '',
      });
      fetchData();
    } catch (error) {
      console.error('Failed to create client:', error);
    }
  };

  const getClientProjects = (clientId) => {
    return projects.filter(p => p.client_id === clientId);
  };

  const filteredClients = clients.filter(client =>
    client.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.primary_contact_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-syne font-bold text-white">Clients</h1>
            <p className="text-fp-textMuted text-sm mt-1">{clients.length} total clients</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fp-textMuted" />
              <Input
                placeholder="Search clients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-fp-surface border-white/10 text-white w-64"
                data-testid="clients-search"
              />
            </div>
            <Button
              onClick={() => setCreateOpen(true)}
              className="btn-primary"
              data-testid="create-client-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Client
            </Button>
          </div>
        </div>

        {/* Clients Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredClients.map((client) => (
            <motion.div
              key={client.id}
              className="bg-fp-surface border border-white/5 rounded-sm p-6 hover:border-primary/30 transition-colors"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid={`client-card-${client.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-primary/20 rounded-sm flex items-center justify-center">
                    <Building className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">{client.company_name}</h3>
                    <p className="text-fp-textMuted text-sm">{client.primary_contact_name}</p>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="p-1 hover:bg-white/10 rounded">
                      <MoreVertical className="w-4 h-4 text-fp-textMuted" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-fp-surface border-white/10">
                    <DropdownMenuItem>
                      <ExternalLink className="w-4 h-4 mr-2" />
                      View Details
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <FolderKanban className="w-4 h-4 mr-2" />
                      View Projects
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-fp-textMuted text-sm">
                  <Mail className="w-4 h-4" />
                  <span>{client.primary_contact_email}</span>
                </div>
                <div className="flex items-center gap-2 text-fp-textMuted text-sm">
                  <Phone className="w-4 h-4" />
                  <span>{client.primary_contact_phone}</span>
                </div>
              </div>

              <div className="pt-4 border-t border-white/5">
                <div className="flex items-center justify-between">
                  <span className="text-fp-textMuted text-sm">Active Projects</span>
                  <span className="text-primary font-semibold">
                    {getClientProjects(client.id).length}
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Create Client Modal */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="bg-fp-surface border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white font-syne">Add New Client</DialogTitle>
            </DialogHeader>
            <form onSubmit={createClient} className="space-y-4">
              <div>
                <Label className="text-fp-textMuted">Company Name</Label>
                <Input
                  value={newClient.company_name}
                  onChange={(e) => setNewClient({ ...newClient, company_name: e.target.value })}
                  className="mt-1 bg-fp-bg border-white/10 text-white"
                  required
                  data-testid="input-company-name"
                />
              </div>
              <div>
                <Label className="text-fp-textMuted">Primary Contact Name</Label>
                <Input
                  value={newClient.primary_contact_name}
                  onChange={(e) => setNewClient({ ...newClient, primary_contact_name: e.target.value })}
                  className="mt-1 bg-fp-bg border-white/10 text-white"
                  required
                  data-testid="input-contact-name"
                />
              </div>
              <div>
                <Label className="text-fp-textMuted">Email</Label>
                <Input
                  type="email"
                  value={newClient.primary_contact_email}
                  onChange={(e) => setNewClient({ ...newClient, primary_contact_email: e.target.value })}
                  className="mt-1 bg-fp-bg border-white/10 text-white"
                  required
                  data-testid="input-contact-email"
                />
              </div>
              <div>
                <Label className="text-fp-textMuted">Phone</Label>
                <Input
                  value={newClient.primary_contact_phone}
                  onChange={(e) => setNewClient({ ...newClient, primary_contact_phone: e.target.value })}
                  className="mt-1 bg-fp-bg border-white/10 text-white"
                  required
                  data-testid="input-contact-phone"
                />
              </div>
              <Button type="submit" className="w-full btn-primary" data-testid="submit-client-btn">
                Create Client
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default ClientsPage;
